import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2, RefreshCw, X } from "lucide-react";

import apiClient from "../../lib/apiClient";
import { dropRepeats, groupLines } from "./scrollSync";

/**
 * Fetch a PDF from the API. With a filename it downloads; without one it
 * returns an object URL for an <iframe> (the caller revokes it).
 */
export async function downloadBlob(url, filename, { method = "get", body } = {}) {
  // The response interceptor hands back response.data, so this IS the Blob.
  let data;
  try {
    data = method === "post"
      ? await apiClient.post(url, body || {}, { responseType: "blob" })
      : await apiClient.get(url, { responseType: "blob" });
  } catch (err) {
    // With responseType blob, the server's JSON error arrives as a Blob too.
    let msg = "That did not work";
    try { msg = JSON.parse(await err.response.data.text()).message || msg; } catch { /* keep */ }
    throw new Error(msg);
  }
  const blob = data instanceof Blob ? data : new Blob([data], { type: "application/pdf" });
  if (blob.type && blob.type.includes("json")) {
    // An error came back as a blob — surface its message.
    const txt = await blob.text();
    let msg = "That did not work";
    try { msg = JSON.parse(txt).message || msg; } catch { /* not JSON */ }
    throw new Error(msg);
  }
  if (!filename) return URL.createObjectURL(blob);
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 1000);
  return null;
}

/**
 * Portalled to <body> with a z-index above antd's layers (1000+), so nothing
 * in the app layout — a transformed ancestor, a higher-stacked header — can
 * hide it. Rendered in place it could be covered, and Preview PDF looked dead.
 */
export function PdfPreviewModal({ url, title, onClose }) {
  if (!url) return null;
  return createPortal(
    <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="flex h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl dark:bg-gray-900"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-2 dark:border-gray-700">
          <span className="text-sm font-medium text-gray-900 dark:text-white">{title}</span>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-900 dark:hover:text-white">
            <X className="h-5 w-5" />
          </button>
        </div>
        <iframe title={title} src={url} className="h-full w-full" />
      </div>
    </div>,
    document.body,
  );
}

/**
 * Draw every page of the PDF at `url` as a canvas `width` px wide, and read
 * where each line of text sits ({ text, page, frac } — frac of the page
 * height) so the editor can scroll to the same paragraph. pdf.js is loaded on
 * first use so it stays out of the main bundle.
 */
async function renderPages(url, width) {
  const pdfjs = await import("pdfjs-dist/webpack");
  const doc = await pdfjs.getDocument(url).promise;
  try {
    // At least 2x the pane's pixels, so small type stays crisp; capped, as
    // every page is a full canvas held in memory.
    const ratio = Math.min(3, Math.max(2, window.devicePixelRatio || 1));
    const canvases = [];
    const lines = [];
    for (let n = 1; n <= doc.numPages; n += 1) {
      const pg = await doc.getPage(n);
      if (pg.getTextContent) {
        const base = pg.getViewport({ scale: 1 });
        const { items } = await pg.getTextContent();
        const placed = items.map((it) => {
          const [x, y] = base.convertToViewportPoint(it.transform[4], it.transform[5]);
          return { str: it.str, x, top: y - (it.height || 0) };
        });
        for (const l of groupLines(placed)) lines.push({ text: l.text, page: n, frac: l.top / base.height });
      }
      const vp = pg.getViewport({ scale: (width / pg.getViewport({ scale: 1 }).width) * ratio });
      const canvas = document.createElement("canvas");
      canvas.width = vp.width;
      canvas.height = vp.height;
      canvas.dataset.page = String(n);
      canvas.className = "mb-2 block w-full bg-white shadow-sm last:mb-0";
      await pg.render({ canvasContext: canvas.getContext("2d"), viewport: vp }).promise;
      canvases.push(canvas);
    }
    return { canvases, lines: dropRepeats(lines) };
  } finally {
    doc.destroy();
  }
}

/**
 * The real PDF next to the editor, rebuilt a moment after the text stops
 * changing. `build()` returns an object URL (downloadBlob without a filename);
 * `contentKey` is anything that changes when the document does.
 *
 * Only the newest answer is shown — a slow build that returns after a faster,
 * later one is dropped — and the previous PDF stays up while the next one is
 * made, so the pane never goes blank mid-edit. `page` opens the first PDF on
 * the page the cursor is on.
 *
 * Pages are drawn with pdf.js into our own scrolling box, so the editor can
 * scroll it (`scrollerRef`, `onScroll`, and `onRendered(lines)` once a new PDF
 * is up, with the text lines renderPages read).
 * If pdf.js can't draw it, the browser's own viewer is used instead.
 */
export function LivePdfPreview({ build, contentKey, page = 1, height, delay = 1500,
                                 scrollerRef, onScroll, onRendered }) {
  const [url, setUrl] = useState(null);
  const [status, setStatus] = useState("idle");      // idle | loading | ready | error
  const [error, setError] = useState("");
  const [builtAt, setBuiltAt] = useState(null);
  const seq = useRef(0);
  const urlRef = useRef(null);
  const buildRef = useRef(build);
  const pageRef = useRef(page);
  buildRef.current = build;
  pageRef.current = page;
  const [shownPage, setShownPage] = useState(page);

  const run = useCallback(async () => {
    const id = ++seq.current;
    setStatus("loading");
    try {
      const next = await buildRef.current();
      if (id !== seq.current) { if (next) URL.revokeObjectURL(next); return; }
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
      urlRef.current = next;
      setShownPage(pageRef.current || 1);
      setUrl(next);
      setError("");
      setStatus("ready");
      setBuiltAt(new Date());
    } catch (e) {
      if (id !== seq.current) return;
      setError(e?.message || "Could not build the preview");
      setStatus("error");
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(run, delay);
    return () => clearTimeout(t);
  }, [contentKey, delay, run]);

  useEffect(() => () => {
    seq.current += 1;
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
  }, []);

  const [frame, setFrame] = useState(false);          // fall back to the browser's viewer
  const [drawn, setDrawn] = useState(false);
  const pagesRef = useRef(null);
  const boxRef = useRef(null);
  const onRenderedRef = useRef(onRendered);
  onRenderedRef.current = onRendered;
  const setBox = useCallback((el) => {
    boxRef.current = el;
    if (typeof scrollerRef === "function") scrollerRef(el);
    else if (scrollerRef) scrollerRef.current = el;
  }, [scrollerRef]);

  // Redraw when the pane gets much wider than the pages were drawn for.
  const [redraw, setRedraw] = useState(0);
  const drawnWidth = useRef(0);
  useEffect(() => {
    const box = boxRef.current;
    if (!box || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => {
      const w = box.clientWidth - 16;
      if (drawnWidth.current && w > drawnWidth.current * 1.15) setRedraw((n) => n + 1);
    });
    ro.observe(box);
    return () => ro.disconnect();
  }, [url, frame]);

  useEffect(() => {
    if (!url || frame) return undefined;
    let stale = false;
    const box = boxRef.current;
    const width = Math.max(200, (box?.clientWidth || 600) - 16);
    renderPages(url, width)
      .then(({ canvases, lines }) => {
        if (stale || !pagesRef.current) return;
        const first = !pagesRef.current.childElementCount;
        const max = box ? box.scrollHeight - box.clientHeight : 0;
        const ratio = max > 0 ? box.scrollTop / max : 0;
        pagesRef.current.replaceChildren(...canvases);
        drawnWidth.current = width;
        setDrawn(true);
        if (box) {
          if (first) {
            const at = pagesRef.current.querySelector(`[data-page="${shownPage}"]`);
            box.scrollTop = at ? at.offsetTop : 0;
          } else {
            box.scrollTop = ratio * (box.scrollHeight - box.clientHeight);
          }
        }
        if (onRenderedRef.current) onRenderedRef.current(lines);
      })
      .catch(() => { if (!stale) setFrame(true); });
    return () => { stale = true; };
  }, [url, frame, redraw]);   // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="flex flex-col overflow-hidden rounded-md border border-gray-300 bg-gray-100 dark:border-gray-600 dark:bg-gray-800"
         style={{ height }} data-testid="live-pdf-preview">
      <div className="flex items-center justify-between gap-2 border-b border-gray-200 bg-white px-2 py-1 text-[11px] text-gray-500 dark:border-gray-700 dark:bg-gray-900">
        <span className="font-medium text-gray-700 dark:text-gray-200">PDF preview</span>
        <span className="flex items-center gap-2">
          {status === "loading" && <span className="inline-flex items-center gap-1 text-indigo-600"><Loader2 className="h-3 w-3 animate-spin" /> Updating…</span>}
          {status === "ready" && builtAt && <span>Updated {builtAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</span>}
          {status === "error" && <span className="text-red-600" title={error}>Preview failed</span>}
          <button type="button" onClick={run} title="Rebuild now"
                  className="rounded p-0.5 text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-700">
            <RefreshCw className="h-3.5 w-3.5" />
          </button>
        </span>
      </div>
      {url && !frame ? (
        <div ref={setBox} onScroll={onScroll} data-testid="pdf-pages"
             className="relative min-h-0 flex-1 overflow-y-auto p-2">
          <div ref={pagesRef} />
          {!drawn && (
            <div className="absolute inset-0 flex items-center justify-center text-xs text-gray-500">
              <span className="inline-flex items-center gap-1"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Drawing the pages…</span>
            </div>
          )}
        </div>
      ) : url ? (
        <iframe key={url} title="PDF preview" src={`${url}#page=${shownPage}&toolbar=0&navpanes=0&view=FitH`}
                className="h-full w-full flex-1 bg-white" />
      ) : (
        <div className="flex flex-1 items-center justify-center p-4 text-center text-xs text-gray-500">
          {status === "error" ? error : <span className="inline-flex items-center gap-1"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Building the PDF…</span>}
        </div>
      )}
    </div>
  );
}
